import Editor from "@draft-js-plugins/editor";
import { ContentBlock, ContentState, EditorState, RichUtils } from "draft-js";
import { debounce } from "lodash";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { usePromise } from "@utils/hooks";
import { mobileOnly } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { useToolbarInfos } from "./hooks";
import { RichTextInputProps, RichTextInputRef } from "./richTextInput";
import { InlineStyle, Toolbar } from "./toolbar";
import { UrlInput } from "./urlInput";
import { PopOverComponent, useDraftPlugins } from "./useDraftPlugins";

export const AdvencedRichInput = forwardRef<RichTextInputRef, RichTextInputProps>(
	(
		{
			value,
			onChange,
			debounceTime,
			onShouldRemove,
			placeholder,
			autoFocus,
			onKeyDown,
			onEnterKey,
			toolbarOffset,
			mentionsEnabled,
			richStyleEnabled = true,
			readOnly = false,
			initialMention,
			...htmlProps
		},
		ref
	) => {
		const { result: draftjsUtil } = usePromise(() => import("draftjs-utils"), []);
		const getSelectionEntity = draftjsUtil?.getSelectionEntity;
		const [editorState, setEditorState] = useState(EditorState.createWithContent(value));
		const editorRef = useRef<Editor>(null);
		const focusEditor = (): NodeJS.Timeout => setTimeout(() => editorRef.current?.focus(), 50);

		useEffect(() => {
			if (autoFocus) {
				focusEditor();
			}
		}, [autoFocus]);

		const convertAndChange = (newState: EditorState): void => onChange(newState.getCurrentContent());
		const debouncedOnChange = useCallback(debounceTime ? debounce(convertAndChange, debounceTime) : convertAndChange, [
			debounceTime,
			onChange,
		]);

		const [urlRequired, setUrlRequired] = useState(false);
		const containerRef = useRef<HTMLDivElement>(null);
		const { toolbarVisible, toolbarPosition } = useToolbarInfos(containerRef);

		const currentEntity = getSelectionEntity
			? editorState.getSelection().getHasFocus() && getSelectionEntity(editorState)
				? editorState.getCurrentContent().getEntity(getSelectionEntity(editorState))
				: undefined
			: undefined;

		const {
			MentionSuggestions,
			EntryComponent,
			plugins,
			open: mentionsOpen,
			setOpen,
			suggestions,
			onSearchChange,
		} = useDraftPlugins(mentionsEnabled);

		useEffect(() => {
			debouncedOnChange(editorState);
		}, [editorState]);

		const handleKeyCommand = (command: string, state: EditorState): "handled" | "not-handled" => {
			if (!richStyleEnabled) {
				return "not-handled";
			}
			const newState = RichUtils.handleKeyCommand(state, command);
			if (newState) {
				setEditorState(newState);
				return "handled";
			}
			return "not-handled";
		};

		const addEmptyLink = (): void => {
			const emptyLink = currentEntity?.getData().url.length === 0;
			const selectionEntityKey = getSelectionEntity?.(editorState);

			if (!(currentEntity?.getType() === "LINK") || emptyLink) {
				setUrlRequired(true);
			}

			if (selectionEntityKey && !emptyLink) {
				setEditorState(RichUtils.toggleLink(editorState, editorState.getSelection(), null));
			} else {
				const content = editorState.getCurrentContent();
				const contentWithEntity = content.createEntity("LINK", "MUTABLE", {
					url: "",
				});
				const newEditorState = EditorState.push(editorState, contentWithEntity, "apply-entity");
				const entityKey = contentWithEntity.getLastCreatedEntityKey();
				setEditorState(RichUtils.toggleLink(newEditorState, editorState.getSelection(), entityKey));
			}
		};

		const toolbarProps = {
			currentStyle: editorState.getCurrentInlineStyle(),
			currentEntity: currentEntity,
			onStyleChosen: (style: InlineStyle) => {
				if (style === "LINK") {
					addEmptyLink();
				} else {
					setEditorState(
						style === "LIST"
							? RichUtils.toggleBlockType(editorState, "unordered-list-item")
							: RichUtils.toggleInlineStyle(editorState, style)
					);
				}
			},
		};

		const urlInputProps = {
			entity: currentEntity,
			onChange: (url: string) => {
				const entityKey = getSelectionEntity?.(editorState);
				if (entityKey) {
					const content = editorState.getCurrentContent();
					const contentWithEntity = content.replaceEntityData(entityKey, { url });
					const newEditorState = EditorState.push(editorState, contentWithEntity, "apply-entity");
					setEditorState(RichUtils.toggleLink(newEditorState, editorState.getSelection(), entityKey));
				}
			},
			onBlur: () => setUrlRequired(false),
		};

		useImperativeHandle(ref, () => ({
			empty: () => {
				const newState = EditorState.push(editorState, ContentState.createFromText(""), "remove-range");
				setEditorState(EditorState.moveFocusToEnd(newState));
			},
			focus: focusEditor,
		}));

		const handleReturn = (e: React.KeyboardEvent): "handled" | "not-handled" => {
			if (onEnterKey && !mentionsOpen) {
				return onEnterKey(e) ? "handled" : "not-handled";
			}
			return "not-handled";
		};

		return (
			<Container {...htmlProps} ref={containerRef}>
				<Toolbar {...toolbarProps} visibleToolbar />
				<Separator />
				<Editor
					ref={editorRef}
					plugins={plugins}
					placeholder={
						value.hasText() || (value.getBlockMap().first() as ContentBlock | undefined)?.getType?.() !== "unstyled"
							? ""
							: placeholder
					}
					editorState={editorState}
					onChange={setEditorState}
					handleReturn={handleReturn}
					handleKeyCommand={handleKeyCommand}
					readOnly={readOnly}
					stripPastedStyles={true}
				/>
				{!!MentionSuggestions && (
					<MentionSuggestions
						open={mentionsOpen}
						onOpenChange={setOpen}
						suggestions={suggestions}
						onSearchChange={onSearchChange}
						popoverComponent={<PopOverComponent />}
						entryComponent={EntryComponent}
					/>
				)}
				{urlRequired ? (
					<InlineUrlInput {...toolbarPosition} {...urlInputProps} />
				) : richStyleEnabled && toolbarVisible ? (
					<InlineToolbar visibleLink {...toolbarPosition} {...toolbarProps} />
				) : null}
				{urlRequired ? <MobileUrlInput {...toolbarPosition} {...urlInputProps} /> : null}
			</Container>
		);
	}
);

const Container = styled.div`
	position: relative;
`;

const Separator = styled.div`
	height: 1px;
	background-color: ${({ theme }) => theme.colors.gray};
	position: absolute;
	width: 95%;
	left: calc((100% - 95%) / 2);
`;

const inlineTooltipStyle = (x: number, y: number): ReturnType<typeof css> => css`
	position: absolute;
	left: ${x}px;
	top: ${y - 10}px;
	transform: translate(-50%, -100%);
	z-index: ${Z.over};

	@media (max-width: 900px) {
		display: none;
	}
`;

const InlineToolbar = styled(Toolbar)<{ x: number; y: number }>`
	${({ x, y }) => inlineTooltipStyle(x, y)};
`;

const InlineUrlInput = styled(UrlInput)<{ x: number; y: number }>`
	${({ x, y }) => inlineTooltipStyle(x, y)};
	width: 226px;
	z-index: ${Z.above(Z.over)};
`;

const mobileTooltipStyle = (toolbarOffset: number): ReturnType<typeof css> => css`
	${mobileOnly("flex")};
	z-index: ${Z.toolbar};
	position: fixed;
	top: initial;
	left: 0;
	bottom: ${toolbarOffset}px;
	right: 0;
	transform: none;
	box-shadow: 0 2px 54px 0 rgba(0, 0, 0, 0.12);
	border: none;
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
	padding: 16px;

	&::after {
		display: none;
	}
`;

const MobileUrlInput = styled(UrlInput)<{ toolbarOffset?: number }>`
	${({ toolbarOffset = 0 }) => mobileTooltipStyle(toolbarOffset)};
`;
